// GroupScreen.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { MdCategory } from "react-icons/md";

import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { useGroupScreen } from "./useGroupScreen";
import { useSwipeNavigation } from "../../common/hooks/useSwipeNavigation";
import { SwipeableScreen } from "../../common/components/swipeableScreen/SwipeableScreen";
import { useTheme } from "../../common/hooks/useTheme";
import { useRatingModal } from "../../common/components/ratingModal/useRatingModal";
import styles from "./GroupScreen.module.scss";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import ImageGallery from "../../common/components/imageGallery/ImageGallery";
import { shortenText } from "../../common/utils/functions/shortenText";
import { SaleItemAttributes } from "../../common/components/saleItemAttributes/SaleItemAttributes";
import { ProductRatingSection } from "../../common/productRatingSection/ProductRatingSection";
import { StockDetails } from "../../common/stockDetails/StockDetails";
import { ItemRelations } from "../../common/components/itemRalations/ItemRelations";
import { CartSection } from "../../common/components/cartSection/CartSection";
import { Share } from "../../common/components/share/Share";
import { IdBadge } from "../../common/components/idBadge/IdBage";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../store/slices/storeListSlice";
import { ROUTES } from "../../common/utils/constants";
import { formatPriceToLocale } from "../../common/utils/functions/formatPriceToLocale";
import { useCartActions } from "../../common/hooks/useCartActions";

export default function GroupScreen() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const { openModal } = useRatingModal();
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);

  const {
    currentVariant,
    currentPrice,
    quantity,
    getUnitName,
    handleOptionSelect,
    handleAddToCart,
    handleRemove,
    handleIncrease,
    handleDecrease,
    selectedOptions,
    currentImages,
    handleFavoritePress,
    canRateVariant,
    currentItem,
    isOutOfStock,
    getCategoryName,
    group,
    availability,
  } = useGroupScreen();

  const { hasPrev, hasNext, prevItem, nextItem } = useSwipeNavigation(
    currentItem?.id || Number(id),
    currentItem?.categoryId ? Number(currentItem.categoryId) : undefined,
  );

  const {
    isComplexCategory,
    showComplexButton,
    showCartButton,
    handleAddToComplex,
    isInComplexCart,
  } = useCartActions(
    currentVariant || ({ id: 0, name: "", prices: [], files: [] } as any),
  );

  if (!currentItem) return null;

  const isFavorite = currentItem?.selected && currentItem.selected.length > 0;
  const hasUserRating = currentItem?.ratings && currentItem.ratings.length > 0;

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta
        itemName={currentItem.name}
        itemDescription={currentItem.description}
        itemImageFile={currentImages[0]}
        itemPrice={currentPrice}
        itemId={currentItem.id}
      />
      <ProductHeader
        title={currentItem.name}
        isFavorite={Boolean(isFavorite)}
        onFavoritePress={handleFavoritePress}
        showFavorite={currentItem.selected !== undefined && !!currentUser}
        textColor={theme.text}
        borderColor={theme.border}
        rightContent={<Share item={currentItem} />}
      />

      <SwipeableScreen
        hasPrev={hasPrev}
        hasNext={hasNext}
        prevPath={prevItem ? `${ROUTES.ITEM}/${prevItem.id}` : undefined}
        nextPath={nextItem ? `${ROUTES.ITEM}/${nextItem.id}` : undefined}
      >
        <div className={styles.mobileGallery}>
          <div className={styles.imageContainer}>
            <ImageGallery images={currentImages} />
            {/* <IdBadge id={currentItem.id} /> */}
          </div>
        </div>

        <div className={styles.scrollView}>
          <div className={styles.content}>
            <div className={styles.leftColumn}>
              <div className={styles.imageContainer}>
                <ImageGallery images={currentImages} />
                <IdBadge id={currentItem.id} />
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.nameRow}>
                <h1 className={styles.name}>{currentItem.name}</h1>
                <span className={styles.price}>
                  {currentPrice
                    ? `${formatPriceToLocale(currentPrice)} ₽`
                    : showComplexButton
                      ? ""
                      : "Нет цены"}
                </span>
              </div>

              <div className={styles.descriptionContainer}>
                <h3 className={styles.descriptionTitle}>Описание</h3>
                <p className={styles.descriptionText}>
                  {currentItem.description}
                </p>
              </div>

              <div className={styles.categoryContainer}>
                <MdCategory
                  size={18}
                  color={theme.primary}
                  className={styles.categoryIcon}
                />
                <span className={styles.categoryText}>
                  {shortenText(
                    getCategoryName(Number(currentItem.categoryId)),
                    30,
                  )}
                </span>
              </div>

              {group?.selectors && Object.keys(group.selectors).length > 0 && (
                <div className={styles.selectorsContainer}>
                  <h3 className={styles.descriptionTitle}>Выберите вариант</h3>
                  {Object.entries(group.selectors).map(
                    ([selectorKey, selector]: [string, any]) => (
                      <div key={selectorKey} className={styles.selectorGroup}>
                        <h4 className={styles.selectorTitle}>
                          {selector.name}{" "}
                          {selector.unit !== -1 &&
                            `(${getUnitName(selector.unit)})`}
                        </h4>
                        <div className={styles.selectorOptions}>
                          {selector.values.map((value: any) => {
                            const isActive =
                              selectedOptions[selectorKey] === value;
                            return (
                              <button
                                key={String(value)}
                                className={`${styles.selectorOption} ${isActive ? styles.selectorOptionActive : ""}`}
                                onClick={() =>
                                  handleOptionSelect(selectorKey, value)
                                }
                              >
                                <span
                                  className={`${styles.selectorOptionText} ${isActive ? styles.selectorOptionTextActive : ""}`}
                                >
                                  {value}{" "}
                                  {selector.unit !== -1 &&
                                    getUnitName(selector.unit)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}

              {currentItem.attributes?.length > 0 && (
                <div className={styles.attributesContainer}>
                  <SaleItemAttributes
                    attributes={currentItem.attributes}
                    view
                  />
                </div>
              )}

              <div className={styles.infoGrid}>
                {currentVariant && (
                  <>
                    <div className={styles.infoBlock}>
                      <ProductRatingSection
                        item={currentItem}
                        canRate={canRateVariant}
                        hasUserRating={Boolean(hasUserRating)}
                        onRatePress={() => openModal(currentVariant.id!)}
                        backgroundColor={theme.card}
                        textColor={theme.text}
                      />
                    </div>

                    <div className={styles.infoBlock}>
                      <StockDetails
                        stockStores={currentVariant.stock_stores}
                        stockEnabled={currentVariant.useStock}
                        title="Наличие в магазинах"
                        forceStore={currentCluster?.forceStore}
                        itemStops={currentVariant.item_stops}
                        selectedStoreIds={selectedStoreIds}
                        availabilityStatus={availability.status}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className={styles.relationsWrapper}>
                <ItemRelations
                  addons={currentVariant?.addons}
                  recommendations={currentVariant?.recommendations}
                  getCategoryName={getCategoryName}
                />
              </div>

              <div className={styles.cartWrapper}>
                <CartSection
                  price={currentPrice}
                  unit={currentVariant?.unit}
                  quantity={quantity}
                  onAdd={handleAddToCart}
                  onIncrease={handleIncrease}
                  onDecrease={handleDecrease}
                  onRemove={handleRemove}
                  layout="group"
                  isOutOfStock={isOutOfStock}
                  isComplexCategory={isComplexCategory}
                  showComplexButton={showComplexButton}
                  showCartButton={showCartButton}
                  onAddToComplex={handleAddToComplex}
                  isInComplexCart={isInComplexCart}
                />
              </div>
            </div>
          </div>
        </div>
      </SwipeableScreen>
    </div>
  );
}
